import { useStateContext } from '@/context/StateContext';
import { calculatePrice } from '@/data/products';
import { Select } from 'antd';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import Counter from './Counter';
import styles from './DigiCertMarkPricing.module.scss';

const { Option } = Select;

const DigiCertMarkPricing = ({ products }) => {
  const { onAdd, setShowCart } = useStateContext();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [prices, setPrices] = useState({});

  useEffect(() => {
    const newPrices = {};
    products.forEach((product) => {
      const options = selectedOptions[product.id] || { domain: 1, logo: 1, year: 1 };
      newPrices[product.id] = calculatePrice(product, options);
    });
    setPrices(newPrices);
  }, [products, selectedOptions]);

  const handleOptionChange = (productId, field, value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || { domain: 1, logo: 1, year: 1 }),
        [field]: value,
      },
    }));
  };

  const handleAddToCart = (product) => {
    const options = selectedOptions[product.id] || { domain: 1, logo: 1, year: 1 };
    onAdd(product, 1, options);
    setShowCart(true);
  };

  const formatPrice = (value) => {
    if (value === undefined || value === null) return 'N/A';
    const numValue = Number(value);
    return isNaN(numValue)
      ? 'N/A'
      : numValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  };

  const renderFeature = (feature, product, index) => {
    if (typeof feature === 'object' && feature.type === 'counter') {
      return (
        <div key={index} className="feature-wrapper">
          <div className="text-200 medium color-neutral-800">
            <Counter
              value={selectedOptions[product.id]?.[feature.name.toLowerCase()] || feature.default}
              onChange={(value) =>
                handleOptionChange(product.id, feature.name.toLowerCase(), value)
              }
              rightLabel={feature.name}
              isSmall={true}
              max={feature.max || Infinity}
            />
          </div>
        </div>
      );
    } else {
      return (
        <div key={index} className="feature-wrapper">
          <div className="line-rounded-icon feature-icon"></div>
          <div className="text-200 medium color-neutral-800">{feature}</div>
        </div>
      );
    }
  };

  const renderPricingCard = (product) => {
    const options = selectedOptions[product.id] || { domain: 1, logo: 1, year: 1 };
    const price = prices[product.id] || {};

    const displayPrice = price.monthlyPrice || 0;
    const displayMSRP = price.msrpFormatted || '$0';
    const annualPrice = price.annualPrice || 0;

    // Convert years to months
    const subscriptionMonths = options.year * 12;

    // Filter and modify features
    const modifiedFeatures = product.features.filter(
      (feature) =>
        typeof feature === 'string' || (typeof feature === 'object' && feature.name !== 'Logo')
    );

    return (
      <div key={product.id} role="listitem" className="card-product-item w-dyn-item">
        <div
          className={`card product ${product.id === 'verified-mark-certificate' ? 'premium' : ''}`}
          style={{ position: 'relative', overflow: 'visible', paddingTop: '32px' }}
        >
          {/* Card content */}
          <div className="card-product-top" style={{ position: 'relative', zIndex: 1 }}>
            {/* Product title, image, and price */}
            <div className="mg-bottom-16px">
              <div className="d-flex align-items-center mg-bottom-16px">
                <Image src={product.icon} alt={`${product.name} Symbol`} width={60} height={60} />
                {product.id === 'verified-mark-certificate' && (
                  <Image
                    src="/images/bm-symbol-check.svg"
                    alt="Verified Mark Check"
                    width={50}
                    height={50}
                    style={{ marginLeft: '8px' }}
                  />
                )}
              </div>
              <div className="card-product-title-wrapper">
                <h2 className="text-400 bold color-accent-1" style={{ marginBottom: '12px' }}>
                  {product.name}
                </h2>
                <div className="flex-vertical children-wrap">
                  <div
                    className="text-300 color-neutral-600"
                    style={{ textDecoration: 'line-through', marginBottom: '8px' }}
                  >
                    MSRP {displayMSRP}
                  </div>
                  <div className="text-400 bold color-neutral-800">
                    ${formatPrice(displayPrice)}/month
                  </div>
                </div>
              </div>
            </div>
            {/* Product description */}
            <div className="inner-container _322px" style={{ minHeight: '140px' }}>
              <p className="mg-bottom-0" style={{ fontSize: '1.1rem' }}>
                {product.description}{' '}
                <Link href={product.learnMoreLink} className="color-accent-1">
                  Learn more about {product.abbreviation}.
                </Link>
              </p>
            </div>
            {/* Product controls */}
            <div className="mg-top-48px">
              {product.features
                .filter((feature) => typeof feature === 'object' && feature.type === 'counter')
                .map((feature, index) => (
                  <div key={index} className={index > 0 ? 'mg-top-16px' : ''}>
                    {renderFeature(feature, product, index)}
                  </div>
                ))}
            </div>
          </div>
          <div
            className="divider card-product"
            style={{ marginBottom: '12px', marginTop: '12px' }}
          ></div>
          <div className="card-product-bottom">
            {/* What's included */}
            <div className="mg-bottom-24px">
              <div className="text-200 bold color-accent-1">What's included</div>
            </div>
            <div className="mg-bottom-38px" style={{ height: '180px' }}>
              <div className="w-layout-grid grid-1-column">
                {modifiedFeatures.map((feature, index) =>
                  typeof feature === 'string' ? renderFeature(feature, product, index) : null
                )}
              </div>
            </div>
            {/* Who's it for */}
            <div className="mg-bottom-24px">
              <div className="text-200 bold color-accent-1">Who's it for</div>
            </div>
            <div className="mg-bottom-38px" style={{ height: '100px' }}>
              <div className="w-layout-grid grid-1-column">
                {product.forWho.map((item, index) => (
                  <div key={index} className="feature-wrapper">
                    <div className="line-rounded-icon feature-icon"></div>
                    <div className="text-200 medium color-neutral-800">{item}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* What's required */}
            <div className="mg-bottom-24px">
              <div className="text-200 bold color-accent-1">What's required</div>
            </div>
            <div className="mg-bottom-38px" style={{ height: '90px' }}>
              <div className="w-layout-grid grid-1-column">
                {product.required.map((item, index) => (
                  <div key={index} className="feature-wrapper">
                    <div className="line-rounded-icon feature-icon"></div>
                    <div className="text-200 medium color-neutral-800">{item}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Price display */}
            <div className="mg-bottom-40px">
              <div className="mg-bottom-12px">
                <div className="card-product-price-display">
                  <div className="display-2">$</div>
                  <div className="display-2">{formatPrice(displayPrice)}</div>
                  <div className="text-200 mg-bottom-12px">/month</div>
                </div>
              </div>
              <div className="text-200 medium color-neutral-600">
                Auto-renew subscription @ ${formatPrice(annualPrice * (options.year || 1))} billed
                every {subscriptionMonths} months
              </div>
            </div>
            {/* Buy now button */}
            <div className="mg-top-auto">
              <button
                className="btn-primary w-button width-100"
                onClick={() => handleAddToCart(product)}
              >
                Buy a {product.abbreviation} Now
                <span className="line-rounded-icon link-icon-right"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div id="pricing" className={`${styles.pricingWrapper} w-dyn-items`}>
      {products.map(renderPricingCard)}
    </div>
  );
};

export default DigiCertMarkPricing;
